import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import Sidebar from './components/layout/Sidebar';
import routes from './routes';
import styled, { createGlobalStyle } from 'styled-components';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MaintenancePopup from './components/common/MaintenancePopup';
import { getSettings } from './api/services/AdminService';

/* =======================
   PRIVATE ROUTE
======================= */
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <LoaderContainer>
        <Spinner />
      </LoaderContainer>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/* =======================
   SUPER ADMIN ROUTE
======================= */
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <LoaderContainer>
        <Spinner />
      </LoaderContainer>
    );
  }

  const isSuperAdmin = user?.role === 'superadmin' || user?.email === 'superadmin@ibm.com';

  if (!isSuperAdmin) {
    return <Navigate to="/applications" replace />;
  }

  return children;
};

/* =======================
   DASHBOARD LAYOUT
======================= */
const DashboardLayout = ({ children }) => {
  return (
    <DashboardContainer>
      <Sidebar />
      <MainContentWrapper>
        <MainContent>{children}</MainContent>
      </MainContentWrapper>
    </DashboardContainer>
  );
};

/* =======================
   PUBLIC LAYOUT
======================= */
const PublicLayout = ({ children }) => {
  return (
    <PublicContainer>
      <PublicContent>{children}</PublicContent>
    </PublicContainer>
  );
};

/* =======================
   APP
======================= */
const GlobalMaintenanceCheck = () => {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        setIsMaintenance(res.data?.isMaintenanceMode || false);
      } catch (err) {
        console.error("Error fetching settings:", err);
      }
    };
    fetchSettings();
  }, [location.pathname]); // re-check on navigate optional, but keep it simple

  if (isMaintenance && location.pathname === '/') {
    return <MaintenancePopup />;
  }
  return null;
};

function App() {
  return (
    <Router>
      <GlobalStyles />
      <AuthProvider>
        <GlobalMaintenanceCheck />
        <Toaster position="top-right" reverseOrder={false} />

        <Routes>
          {routes.map((route, index) => {
            // Admin protected routes
            if (route.requiresAuth) {
              const RouteWrapper = route.requiresSuperAdmin ? SuperAdminRoute : PrivateRoute;

              return (
                <Route
                  key={index}
                  path={route.path}
                  element={
                    <RouteWrapper>
                      <DashboardLayout>
                        {route.element}
                      </DashboardLayout>
                    </RouteWrapper>
                  }
                />
              );
            }

            // Public routes (including /login)
            return (
              <Route
                key={index}
                path={route.path}
                element={
                  <PublicLayout>
                    {route.element}
                  </PublicLayout>
                }
              />
            );
          })}

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

/* =======================
   GLOBAL STYLES
======================= */
const GlobalStyles = createGlobalStyle`
  :root {
    --primary-color: #182848;
    --primary-light: #27496d;
    --accent-color: #4cc9f0;
    --light-color: #f5f9ff;
    --dark-color: #0f1b2b;
    --success-color: #38b000;
    --danger-color: #f72585;
    --warning-color: #ffb703;
    --info-color: #00b4d8;
    --sidebar-width: 250px;
    --sidebar-collapsed-width: 80px;
    --transition: all 0.3s ease;
    --border-radius: 8px;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f8f9fa;
  }

  .content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #dee2e6;

    h1 {
      font-size: 1.8rem;
      color: var(--dark-color);
      margin: 0;
    }
  }
`;

/* =======================
   STYLED COMPONENTS
======================= */
const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f7fa;
`;

const MainContentWrapper = styled.div`
  flex-grow: 1;
  padding-left: var(--sidebar-width);
  transition: var(--transition);
  width: 100%;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 1.5rem;
  background-color: var(--light-color);
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PublicContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const PublicContent = styled.main`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LoaderContainer = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default App;
